import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Form, Formik} from 'formik';
import {FormattedMessage, injectIntl} from 'react-intl';

import {Grid} from '@mui/material';

import Card from 'components/page/Card';
import CardBody from 'components/card/cardBody';
import GridContainer from 'components/grid/gridContainer';
import GridItem from 'components/grid/gridItem';
import CardTitle from 'components/card/CardTitle';
import CardHeader from 'components/card/cardHeader';
import SaveButton from 'components/button/SaveButton';

import {postRetailerTranslationTag, updatePatch} from 'api/retailer/update';
import show from 'api/retailer/show';
import {handleFileUpload} from 'api/upload';
import TagImageDropzone from 'views/tag/components/TagImageDropzone';

import FormBloc from "../../../components/form/FormBloc";
import FormTranslator from "../../../components/translator/formTranslator";
import Label from "../../../components/input/new/Label";
import InputTextField from "../../../components/input/new/InputTextField";
import CustomSwitch from "../../../components/switch/CustomSwitch";
import * as yup from "yup";
import {lazy} from "yup";
import mapValues from "lodash/mapValues";
import store from "../../../store";
import {RetailersTagPosition} from "../../tag/components/tagPosition";
import SelectTitle from "../../../components/radioGroup/SelectTitle";
import RadioGroup from "../../../components/radioGroup/RadioGroup";
import FormControlLabel from "../../../components/radioGroup/FormControlLabel";
import getTranslationInitialValues from "../../../utils/getTranslationInitialValues";
import ActionsContainer from "../../../components/containers/ActionsContainer";
import populateMissingTranslations from "../../../utils/populateMissingTranslations";
import {changeSlide} from "react-slick/lib/utils/innerSliderUtils";
import {create} from "../../../api/retailerTranslations/create";
import {TagVisibility, TagVisibilityTranslationKey} from "../../../utils/tagVisibility";
import useIsMobile from "hooks/useIsMobile";

Tags.propTypes = {
    retailer: PropTypes.object.isRequired
};

function Tags({retailer, ...props}) {
    const [options, setOptions] = useState([]);
    const [retailerData, setRetailerData] = useState(retailer);
    const [loading, setLoading] = useState(false);
    const [isSuccess, setSuccess] = useState(false);
    const [tagPosition, setTagPosition] = useState([])
    const [tagVisibility, setTagVisibility] = useState([])
    const [tagColor, setTagColor] = useState([])
    const isMobile = useIsMobile()

    const TAG_IMAGE_FIELDS = ['tagImageDesktop', 'tagImageMobile'];
    const TAG_IMAGE_UPLOAD_TYPE = 'retailer_tag_image';

    // Tag (fanion) images per locale and per field: the URL of an already stored image,
    // a File pending upload, or null when no image is set.
    const buildTagImages = (fromRetailer) => {
        const tagImagesTmp = {};
        Object.entries(fromRetailer?.translations ?? {}).forEach(([locale, translation]) => {
            tagImagesTmp[locale] = {
                tagImageDesktop: translation.tagImageDesktop?.contentUrl ?? null,
                tagImageMobile: translation.tagImageMobile?.contentUrl ?? null,
            };
        });
        return tagImagesTmp;
    };
    const [tagImages, setTagImages] = useState(() => buildTagImages(retailer));

    const setTagImage = (locale, field, image) => {
        setTagImages({
            ...tagImages,
            [locale]: {...tagImages[locale], [field]: image},
        });
    };

    // Uploads the pending tag image files, and returns per locale the fields to patch on
    // the translation: the IRI of a new upload, or null for a removed image.
    const buildTagImagesPayloads = async (translations) => {
        const payloads = {};
        const uploadPromises = [];

        Object.keys(translations).forEach((locale) => {
            TAG_IMAGE_FIELDS.forEach((field) => {
                const current = tagImages[locale]?.[field];

                if (current instanceof File) {
                    uploadPromises.push(handleFileUpload([current], TAG_IMAGE_UPLOAD_TYPE).then((mediaObject) => {
                        payloads[locale] = {...payloads[locale], [field]: mediaObject['@id']};
                    }));

                    return;
                }

                if (!current && retailerData?.translations?.[locale]?.[field]) {
                    payloads[locale] = {...payloads[locale], [field]: null};
                }
            });
        });

        await Promise.all(uploadPromises);

        return payloads;
    };

    // The required selects (color, position, visibility) preselect their first available
    // option instead of starting empty and blocking the submit.
    const organizationTagColors = store.getState().currentOrganization?.retrieved?.tagColors ?? {};

    const initialValues = {
        'translations': populateMissingTranslations(retailerData?.translations) || getTranslationInitialValues(),
        'tagEnabled': retailerData.tagEnabled ?? false,
        'tagPriority': retailerData.tagPriority ?? 1,
        'tagColor': retailerData.tagColor ?? (Object.values(organizationTagColors)[0] ?? ''),
        'tagPosition': retailerData.tagPosition ?? (Object.values(RetailersTagPosition)[0] ?? ''),
        'tagVisibility': retailerData.tagVisibility ?? TagVisibility.ALL,
    };

    const validationSchema = yup.object({
        tagEnabled: yup.bool(),
        tagPriority: yup.number()
            .typeError(props.intl.formatMessage({id: 'retailer.form.priority.error.type'}))
            .positive(props.intl.formatMessage({id: 'retailer.form.priority.error.positive'}))
            .min(0)
            .required(props.intl.formatMessage({id: 'retailer.form.priority.error.required'})),
        tagVisibility: yup.string().oneOf(Object.values(TagVisibility)).required(props.intl.formatMessage({id: 'retailer.form.visibility.error.required'})),
        translations: lazy(obj => yup.object(
            mapValues(obj, () => yup.object({
                tagLabel: yup.string().required(props.intl.formatMessage({id: 'list.form.value.required'}))
            }))
        )),
        tagColor: yup.string()
            .required(props.intl.formatMessage({id: 'retailer.form.color.error.required'})),
        tagPosition: yup.string()
            .required(props.intl.formatMessage({id: 'retailer.form.position.error.required'}))
    });
    function saveRetailerTag(values, actions) {

        let update = false

        // Convert tagPriority from string to int
        values.tagPriority = parseInt(values.tagPriority)

        setLoading(true)
        setSuccess(false)

        const translationObject = values.translations
        const {translations, ...newValues} = values

        buildTagImagesPayloads(translations).then((tagImagesPayloads) => {
            let translationPromises = []
            Object.keys(translations).forEach((locale) => {
                const translation = translations[locale]
                const imagesPayload = tagImagesPayloads[locale] ?? {}
                if (!translation['@id']) {
                    translationPromises.push(create(retailer['@id'], {...translation, ...imagesPayload}))
                } else {
                    translationPromises.push(updatePatch(translation, {tagLabel: translation.tagLabel, ...imagesPayload}))
                }
            })

            return Promise.all(translationPromises).then(() => updatePatch(retailer, newValues))
        }).then(() => {
            setSuccess(true);
            const retailerId = retailer['@id'].split('/').pop();
            return show(retailerId).then((updatedRetailer) => {
                setRetailerData(updatedRetailer);
                setTagImages(buildTagImages(updatedRetailer));
            });
        }).catch((error) => {
            // A failed upload or write must not leave the form stuck on a dead submit button.
            console.error(error);
        }).finally(() => {
            actions.setSubmitting(false);
            setLoading(false);
        })
    }

    function getTagNameByLabel(value) {
        return tagPosition.find((tag) => tag.value === value)?.label
    }

    function getTagVisibilityNameByLabel(value) {
        const tagVisibilityLabel = tagVisibility.find((tag) => tag.value === value)?.label
        return tagVisibilityLabel ? <FormattedMessage id={tagVisibilityLabel} /> : null
    }

    function getColorByLabel(value) {
        if (value === '') {
            return ''
        }
        return <FormattedMessage id={"retailer.form.label.tagColor."+value} />
    }

    useEffect(() => {
        // Setting the tag colors
        let colorsInStore = store.getState().currentOrganization?.retrieved?.tagColors ?? []
        if (colorsInStore !== []) {
            let colors = Object.entries(colorsInStore).map(([key, val]) => {
                return {label: key, value: val};
            })
            setTagColor(colors);
        }

        // Setting the tag position
        const formattedOptions = [];
        Object.entries(RetailersTagPosition).forEach(entry => {
            const [name, value] = entry;
            formattedOptions.push({
                'value': value,
                'label': props.intl.formatMessage({id: 'corner.form.label.position.' + name.toLowerCase()})
            });
        });
        setTagPosition(formattedOptions)

        // Setting the tag visibility
        const options = [];
        Object.entries(TagVisibility).forEach(entry => {
            const [name, value] = entry;
            options.push({
                'value': value,
                'label': props.intl.formatMessage({id: TagVisibilityTranslationKey[value]})
            });
        });
        setTagVisibility(options)
    }, []);

    return (
        <Card>
            <CardHeader color="info">
                <GridContainer>
                    <GridItem xs={9} sm={10}>
                        <CardTitle titleId={'retailer.form.title.tabLabel'}/>
                    </GridItem>
                </GridContainer>
            </CardHeader>
            <CardBody
                style={{overflow: "unset"}}
            >
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    enableReinitialize={true}
                    validateOnBlur={true}
                    validateOnChange={true}
                    isInitialValid={false}
                    validateOnMount={true}
                    onSubmit={(values, {setSubmitting}) => {
                        saveRetailerTag(values, {setSubmitting});
                    }}
                >
                    {({
                          isSubmitting,
                          isValid,
                          handleBlur,
                          values,
                          touched,
                          handleChange,
                          errors,
                          setFieldValue
                      }) => (
                        <Form>
                            <Grid container rowSpacing={4}>
                                <Grid item xs={12}>
                                    <CustomSwitch
                                        name="tagEnabled"
                                        checked={values.tagEnabled}
                                        onClick={handleChange}
                                    />
                                    <FormattedMessage id="retailer.form.label.tagEnabled"/>
                                </Grid>
                                <FormBloc titleId={"retailer.form.subtitle.traduction.tagLabel"}>
                                    <Grid container rowSpacing={3}>
                                        <Grid item xs={12}>
                                            <FormTranslator formGenerator={(index, code) => (
                                                <>
                                                    <Label htmlFor={`translations.${code}.tagLabel`}>
                                                        <FormattedMessage id="retailer.form.label.tagLabel"/>
                                                    </Label>
                                                    <InputTextField
                                                        name={`translations.${code}.tagLabel`}
                                                        onChange={handleChange}
                                                        value={values.translations[code]?.tagLabel ?? ''}
                                                        placeholder={props.intl.formatMessage({id: 'retailer.form.placeholder.tagLabel'})}
                                                        fullWidth
                                                        onBlur={handleBlur}
                                                        error={touched.translations?.[code]?.tagLabel && Boolean(errors.translations?.[code]?.tagLabel)}
                                                        helperText={touched.translations?.[code]?.tagLabel && errors.translations?.[code]?.tagLabel}
                                                    />
                                                    {TAG_IMAGE_FIELDS.map((field) => (
                                                        <Grid item xs={12} key={field} sx={{marginTop: 2}}>
                                                            <TagImageDropzone
                                                                label={props.intl.formatMessage({id: `retailer.form.label.${field}`})}
                                                                image={tagImages[code]?.[field]}
                                                                onDrop={(file) => setTagImage(code, field, file)}
                                                                onDelete={() => setTagImage(code, field, null)}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </>
                                            )}/>
                                        </Grid>
                                    </Grid>
                                </FormBloc>
                                <FormBloc titleId={"retailer.form.subtitle.parameter.tagLabel"}>
                                    <Grid container columnSpacing={2} rowSpacing={2} direction={isMobile ? "column" : "row"}>
                                        <Grid item xs={3}>
                                            <Label htmlFor={`retailerTagNamePriority`}>
                                                <FormattedMessage id="retailer.form.label.tagPriority"/>
                                            </Label>
                                            <InputTextField
                                                name={`tagPriority`}
                                                onChange={handleChange}
                                                value={values.tagPriority}
                                                placeholder={props.intl.formatMessage({id: 'retailer.form.placeholder.tagPriority'})}
                                                fullWidth
                                                onBlur={handleBlur}
                                                error={touched.tagPriority && Boolean(errors.tagPriority)}
                                                helperText={touched.tagPriority && errors.tagPriority}
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <SelectTitle titleTranslationKey='retailer.form.label.tagColor'/>
                                            <RadioGroup
                                                alignListWithButton
                                                name={'tagColor'}
                                                selectedValueLabel={getColorByLabel(values.tagColor)}
                                                value={values.tagColor}
                                                onChange={(event) => {
                                                    setFieldValue('tagColor', event.currentTarget.value)
                                                }}
                                            >
                                                {tagColor.map((color, index) => (
                                                    <FormControlLabel
                                                        key={index}
                                                        value={color.value}
                                                        label={<FormattedMessage id={"retailer.form.label.tagColor."+color.value} />}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <SelectTitle titleTranslationKey='retailer.form.label.tagPosition'/>
                                            <RadioGroup
                                                alignListWithButton
                                                name={'tagPosition'}
                                                selectedValueLabel={getTagNameByLabel(values.tagPosition)}
                                                value={values.tagPosition}
                                                onChange={(event) => {
                                                    setFieldValue('tagPosition', event.currentTarget.value)
                                                }}
                                            >
                                                {tagPosition.map((position, index) => (
                                                    <FormControlLabel
                                                        key={index}
                                                        value={position.value}
                                                        label={position.label}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <SelectTitle titleTranslationKey='retailer.form.label.tagVisibility'/>
                                            <RadioGroup
                                                alignListWithButton
                                                name={'tagVisibility'}
                                                selectedValueLabel={getTagVisibilityNameByLabel(values.tagVisibility)}
                                                value={values.tagVisibility}
                                                onChange={(event) => {
                                                    setFieldValue('tagVisibility', event.currentTarget.value)
                                                }}
                                            >
                                                {tagVisibility.map((option, index) => (
                                                    <FormControlLabel
                                                        key={index}
                                                        value={option.value}
                                                        label={<FormattedMessage id={option.label} />}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </Grid>
                                    </Grid>
                                </FormBloc>
                                <Grid item xs={12}>
                                    <ActionsContainer>
                                        <SaveButton
                                            type={"submit"}
                                            disabled={loading || !isValid || isSubmitting}
                                            helperText={props.intl.formatMessage({id: "retailer.update.tag.success"})}
                                            showHelperText={isSuccess}
                                        >
                                            <FormattedMessage id={"retailer.update.tag.save"}/>
                                        </SaveButton>
                                    </ActionsContainer>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </CardBody>
        </Card>
    );
}
export default injectIntl(Tags);
